import {useCallback, useEffect, useState} from 'react';
import {useStore} from 'react-redux';
import {toastAlert} from '~library/atoms/Toast';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {useProcessAffiliateUrlsMutation} from '../api/affiliate';
import {entryHasProductLink} from '../utils/misc';

export const useAffiliateActions = () => {
  const [processAffiliateUrls, {data}] = useProcessAffiliateUrlsMutation({
    fixedCacheKey: 'affiliate-urls',
  });
  const store = useStore();

  const [linkPreviews, setLinkPreviews] = useState({});

  const handleAffiliateError = useCallback((error) => {
    if (!error) return;
    const status = error.status ?? error.originalStatus;
    if (status === 503) {
      evite.warn('Affiliate API returned 503. Continuing without link previews.');
    } else {
      evite.error(error);
    }
    toastAlert(
      'We were unable to load the product links. You can keep editing and try again later.',
      {
        icon: <AlertLineIcon ratio={1.5} />,
        type: 'error',
        position: 'top-right',
        toastId: 'affiliate-links-fetch-error',
      }
    );
  }, []);

  useEffect(() => {
    if (data) setLinkPreviews(data);
  }, [data]);

  const processProductLinks = useCallback(
    async (sos = store.getState().sheet.signup_options) => {
      const urls = {};
      for (const so of sos) {
        if (entryHasProductLink(so)) {
          urls[so.uuid] = {
            url: so.product_link.url,
          };
        }
        for (const slot of so.slots ?? []) {
          if (entryHasProductLink(slot)) {
            urls[slot.slot_id] = {
              url: slot.product_link.url,
            };
          }
        }
      }
      if (Object.keys(urls).length > 0) {
        try {
          const response = await processAffiliateUrls(urls);
          if (response?.error) {
            handleAffiliateError(response.error);
          }
        } catch (error) {
          handleAffiliateError(error);
        }
      }
    },
    [store, processAffiliateUrls, handleAffiliateError]
  );

  return {processProductLinks, linkPreviews, processAffiliateUrls};
};
